import { useState, useMemo } from "react";
import { currencyFormatter } from "../../../../utils/formatter/formatter";
import { SYSTEM_COLORS } from "../../../../components/system.colors";

const TYPE_COLORS = {
  additionalSale: SYSTEM_COLORS.GREEN.FOREST_GREEN,
  optimizeBuy: SYSTEM_COLORS.RED.CORAL_RED,
  stock: SYSTEM_COLORS.RED.BRICK_RED,
  trend: SYSTEM_COLORS.BLUE.OCEAN_BLUE
};

const TYPE_LABELS = {
  additionalSale: "Venda Adicional",
  optimizeBuy: "Otimização de Compra",
  stock: "Estoque",
  trend: "Tendência"
};

export function ProductHierarchyCard({ ctx, actions }) {
  const { detailsByLevel, hierarchy } = ctx || {};
  const { onFocusProduct } = actions || {};

  const [path, setPath] = useState([]);

  const levels = hierarchy?.levels ?? [];
  const labels = hierarchy?.labels ?? {};

  const depth = path.length;
  const levelKey = levels[depth];
  const levelLabel = labels[levelKey] ?? levelKey;
  const isLeafLevel = levelKey === levels[levels.length - 1];

  const children = useMemo(() => {
    const entries = detailsByLevel?.[levelKey] ?? [];
    return entries
      .filter((entry) => path.every((segment, i) => entry.path[i] === segment))
      .sort((b, a) => a.percent - b.percent);
  }, [detailsByLevel, levelKey, path]);

  const currentNode = useMemo(() => {
    if (depth === 0) return null;

    const parentKey = levels[depth - 1];
    return (detailsByLevel?.[parentKey] ?? []).find((entry) => entry.path.join("|") === path.join("|"));
  }, [detailsByLevel, levels, depth, path]);

  const maxValue = useMemo(
    () => Math.max(1, ...children.map((c) => Number(c.totalValue))),
    [children]
  );

  const crumbs = useMemo(() => {
    const list = [
      {
        label: "Todos os níveis de produto",
        path: [],
      },
    ];

    path.forEach((segment, i) => {
      list.push({
        label: segment,
        path: path.slice(0, i + 1),
      });
    });

    return list;
  }, [path]);

  const typesInView = useMemo(() => {
    const set = new Set();

    children.forEach((c) => { (c.types || []).forEach((t) => set.add(t)) });

    return Array.from(set);
  }, [children]);

  const hierarchyText = levels
    .map((level) => labels[level] ?? level)
    .join(" → ");

  const totalValue = currentNode
    ? Number(currentNode.totalValue)
    : children.reduce((sum, c) => sum + Number(c.totalValue), 0);

  return (
    <section
      style={{
        backgroundColor: SYSTEM_COLORS.WHITE.WHITE, border: `1px solid ${SYSTEM_COLORS.GRAY.LIGHT_GRAY}`, borderRadius: "12px", 
        boxShadow: "0 1px 3px rgba(0,0,0,0.04)", padding: 24, display: "flex", 
        flexDirection: "column", flex: 1, minHeight: 0, height: "100%"
      }}>
      {/* Cabeçalho */}
      <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 12 }}>
        <hgroup>
          <h3 style={{ fontSize: "1rem", fontWeight: 800, color: "#1a1a1a", margin: 0, paddingBlockEnd: "0.4rem" }}>
            Oportunidades por Grupo de Produto
          </h3>

          <p style={{ fontSize: "0.75rem", color: SYSTEM_COLORS.GRAY.GREY, margin: "0 0 16px" }}>
            Clique para abrir a hierarquia: {hierarchyText}
          </p>        
        </hgroup>        

        {/* Badge IA */}
        <svg
          width="55" height="25" viewBox="0 0 55 25" fill="none" xmlns="http://www.w3.org/2000/svg"
          style={{ flexShrink: 0, display: "block" }}>
          <rect x="0" y="0" width="55" height="25" rx="12.5" fill={SYSTEM_COLORS.RED.CORAL_RED} />

          <g transform="translate(6.5 4.5) scale(0.67)" stroke={SYSTEM_COLORS.WHITE.WHITE} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M11.017 2.814a1 1 0 0 1 1.966 0l1.051 5.558a2 2 0 0 0 1.594 1.594l5.558 1.051a1 1 0 0 1 0 1.966l-5.558 1.051a2 2 0 0 0-1.594 1.594l-1.051 5.558a1 1 0 0 1-1.966 0l-1.051-5.558a2 2 0 0 0-1.594-1.594l-5.558-1.051a1 1 0 0 1 0-1.966l5.558-1.051a2 2 0 0 0 1.594-1.594z" />
            <path d="M20 2v4" />
            <path d="M22 4h-4" />
            <circle cx="4" cy="20" r="2" />
          </g>

          <text
            x="27" y="16" fill={SYSTEM_COLORS.WHITE.WHITE}
            fontFamily="Arial, sans-serif" fontSize="11" fontWeight="700">
            IA
          </text>
        </svg>
      </div>

      {/* Hierarquia de produto */}
      <div style={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: 6, marginBottom: 12 }}>

        {crumbs.map((crumb, i) => {
          const isLast = i === crumbs.length - 1;

          return (
            <span
              key={`${crumb.label}-${i}`}
              style={{ display: "flex", alignItems: "center", gap: 6 }}>

              {i > 0 && (
                <span style={{ color: SYSTEM_COLORS.GRAY.LIGHT_SILVER, fontSize: "0.75rem" }}>
                  ›
                </span>
              )}

              <button
                onClick={() => setPath(crumb.path)}
                disabled={isLast}
                className={isLast ? "oi-chip oi-chip-active" : "oi-chip" }
                style={{
                  display: "flex", alignItems: "center",
                  gap: 6, border: "none", borderRadius: 20,
                  padding: "5px 12px", fontSize: "0.688rem", fontWeight: 700,
                  background: isLast ? SYSTEM_COLORS.RED.CORAL_RED : SYSTEM_COLORS.WHITE.ALABASTER,
                  color: isLast ? SYSTEM_COLORS.WHITE.WHITE : SYSTEM_COLORS.GRAY.MEDIUM_GRAY,
                  cursor: isLast ? "default" : "pointer" }}>

                {i === 0 && (
                  <svg
                    width={12} height={12} viewBox="0 0 24 24" fill="none"
                    stroke={isLast ? SYSTEM_COLORS.WHITE.WHITE : SYSTEM_COLORS.GRAY.TAUPE_GRAY}
                    strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="m12.83 2.18-9.24 3.9a1 1 0 0 0 0 1.84l8.58 3.91a2 2 0 0 0 1.66 0l8.58-3.9a1 1 0 0 0 0-1.84l-8.58-3.9a2 2 0 0 0-1.66 0Z" />
                    <path d="M2 12a1 1 0 0 0 .58.91l8.6 3.91a2 2 0 0 0 1.65 0l8.58-3.9A1 1 0 0 0 22 12" />
                    <path d="M2 17a1 1 0 0 0 .58.91l8.6 3.91a2 2 0 0 0 1.65 0l8.58-3.9A1 1 0 0 0 22 17" />
                  </svg>
                )}

                {crumb.label}
              </button>
            </span>
          );
        })}

        {path.length > 0 && (
          <button
            onClick={() => setPath(path.slice(0, -1))}
            className="oi-ghost-btn"
            style={{
              marginLeft: "auto", display: "flex", alignItems: "center",
              gap: 4, border: "none", background: "none",
              borderRadius: 8, padding: "4px 8px", color: SYSTEM_COLORS.GRAY.GREY,
              fontSize: "0.719rem", fontWeight: 700, cursor: "pointer" }}>
            <svg
              width={12} height={12} viewBox="0 0 24 24" fill="none"
              stroke="#999" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M20 20v-7a4 4 0 0 0-4-4H4" />
              <path d="M9 14 4 9l5-5" />
            </svg>
            Voltar
          </button>
        )}
      </div>

      {/* Informações financeiras */}
      <div
        style={{
          display: "flex", alignItems: "baseline",
          gap: 8, background: SYSTEM_COLORS.WHITE.WARM_WHITE, border: `1px solid ${SYSTEM_COLORS.GRAY.WARM_GRAY}`,
          borderRadius: 999, padding: "9px 16px", marginBottom: 12 }}>
        <span style={{ fontSize: "0.688rem", fontWeight: 700, color: SYSTEM_COLORS.GRAY.SLATE_GRAY, letterSpacing: 0.4, textTransform: "uppercase" }}>
          {levelLabel}
        </span>

        <span style={{ fontSize: "0.688rem", color: SYSTEM_COLORS.GRAY.NEUTRAL_GRAY }}>
          · {children.length} itens
        </span>

        <span
          style={{
            marginLeft: "auto", fontSize: "0.938rem", fontWeight: 900, color: SYSTEM_COLORS.RED.CORAL_RED,
            fontFamily: "'Arial Black', Arial, sans-serif", letterSpacing: "-0.01em", fontVariantNumeric: "tabular-nums" }}>
          {currencyFormatter.format(totalValue)}
        </span>
      </div>

      {/* Lista */}
      <div
        style={{
          display: "flex", flexDirection: "column",
          gap: 8, flex: 1, minHeight: 0,
          overflowY: "auto", marginRight: -12, paddingRight: 12 }}>

        {children.map((child) => {
          const canDrill = !isLeafLevel;
          const isLeaf = !canDrill;

          const barWidth = Number(child.percent);
          const color = TYPE_COLORS[child.types?.[0]] ?? SYSTEM_COLORS.RED.CORAL_RED;

          return (
            <div
              key={child.path.join("|")}
              onClick={() => canDrill && setPath(child.path)}
              className={canDrill ? "oi-row" : undefined}
              style={{
                display: "grid", gridTemplateColumns: "minmax(0,1fr) auto", alignItems: "center", gap: 12,
                border: `1px solid ${SYSTEM_COLORS.WHITE.PEARL}`, borderRadius: 10, padding: "10px 12px", background: SYSTEM_COLORS.WHITE.WHITE,
                cursor: canDrill ? "pointer" : "default" }}>

              <div style={{ minWidth: 0 }}>
                <div style={{ display: "flex", alignItems: "center", gap: 6, minWidth: 0 }}>
                  <span style={{ width: 7, height: 7, borderRadius: "50%", background: color, flexShrink: 0 }}/>

                  <span
                    style={{
                      fontSize: "0.813rem", fontWeight: 700, color: SYSTEM_COLORS.BLACK.EERIE_BLACK,
                      whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                    {child.name}
                  </span>

                  {canDrill && (
                    <span
                      className="oi-chevron"
                      style={{ display: "flex", flexShrink: 0 }}>
                      <svg
                        width={13} height={13} viewBox="0 0 24 24" fill="none"
                        stroke={SYSTEM_COLORS.GRAY.GREY} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="m9 18 6-6-6-6" />
                      </svg>
                    </span>
                  )}

                  <span style={{ marginLeft: "auto", flexShrink: 0, fontSize: "0.688rem", fontWeight: 600, color: SYSTEM_COLORS.GRAY.GREY }}>
                    {child.percent}%
                  </span>
                </div>

                <div style={{ marginTop: 6, height: 10, borderRadius: 5, background: SYSTEM_COLORS.WHITE.PEARL, overflow: "hidden" }}>
                  <div
                    style={{
                      width: `${barWidth}%`, height: "100%", borderRadius: 5,
                      background: color, opacity: 0.85, transition: "width .4s" }}/>
                </div>
              </div>

              <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                <span
                  style={{
                    minWidth: 64, textAlign: "right",
                    fontSize: "0.75rem", fontWeight: 700, color: SYSTEM_COLORS.BLACK.EERIE_BLACK }}>
                  {currencyFormatter.format(
                    Number(child.totalValue)
                  )}
                </span>

                {isLeaf ? (
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      onFocusProduct?.(child.productId);
                    }}
                    className="oi-outline-btn"
                    style={{
                      display: "flex", alignItems: "center", gap: 4, 
                      border: "none", background: "none",
                      color: SYSTEM_COLORS.RED.CORAL_RED, fontSize: "0.688rem", fontWeight: 700, borderRadius: 8,
                      padding: "4px 10px", cursor: "pointer", whiteSpace: "nowrap" }}>
                    <svg 
                      width={11} height={11}
                      viewBox="0 0 24 24" fill="none" stroke={SYSTEM_COLORS.RED.CORAL_RED}
                      strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M5 12h14" />
                      <path d="m12 5 7 7-7 7" />
                    </svg>

                    Ações
                  </button>
                ) : (
                  <span
                    style={{
                      minWidth: 52,
                      textAlign: "right",
                      fontSize: 11,
                      color: SYSTEM_COLORS.GRAY.GREY,
                    }}
                  >
                    {child.qtdProdutos}{" "}
                    {child.qtdProdutos === 1
                      ? "produto"
                      : "produtos"}
                  </span>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {/* Legenda */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 16,
          borderTop: `1px solid ${SYSTEM_COLORS.GRAY.PLATINUM}`,
          paddingTop: 12,
          marginTop: 12,
          fontSize: 11,
          color: "#999",
          flexWrap: "wrap",
        }}
      >
        {typesInView.map((t) => (
          <span
            key={t}
            style={{
              display: "flex",
              alignItems: "center",
              gap: 6,
            }}
          >
            <span
              style={{
                width: 10,
                height: 10,
                borderRadius: "50%",
                background:
                  TYPE_COLORS[t] ?? SYSTEM_COLORS.GRAY.GREY,
                opacity: 0.85,
              }}
            />

            {TYPE_LABELS[t] ?? t}
          </span>
        ))}

        <span
          style={{
            marginLeft: "auto",
            fontStyle: "italic",
          }}
        >
          {isLeafLevel
            ? "ações disponíveis no item"
            : "clique para abrir o nível seguinte"}
        </span>
      </div>
    </section>
  );
};